import { Component, HostListener, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { SignalrService } from 'src/app/services/signalr/signalr.service';
import { BackendMessage, BackendMessageType } from 'src/app/services/signalr/signalr.types';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { InvoicingCutoffService } from '../../../../services/invoicing/invoicing-cutoff.service';
import { PreInvoicingStateEnums } from './../../../../enum/pre-invoicing-state-enums';
import { PreInvoicingAlertModel } from './../../../../models/invoicing/pre-invoicing-alert.models';


@Component({
  selector: 'app-pre-invoicing',
  templateUrl: './pre-invoicing.component.html',
  styleUrls: ['./pre-invoicing.component.scss'],
  standalone: false
})
export class PreInvoicingComponent implements OnInit, OnDestroy {

  @Input() maxPosition: number = 0;
  @Input() permissionUpdatable: boolean = true;

  branchName: string = "";
  disabledClose: boolean = false;
  disabledStart: boolean = false;
  invoicingCutoffCode: string = "";
  invoicingCutoffDate: string = "";
  invoicingCutoffId: number = 0;
  listObservers: Array<Subscription> = new Array<Subscription>();
  loadPreInvoicingModel: LoadPreInvoicingModel = new LoadPreInvoicingModel();
  preInvoicingAlerts: Array<PreInvoicingAlertModel> = new Array<PreInvoicingAlertModel>();
  progressCurrent: number = 0;
  progressJobId: string | null = null;
  progressFinished: boolean = false;
  progressStatusMessage: string = 'Inicializando...';
  progressValue: number = 0;
  progressTotal: number = 0;
  showPreinvoicingProgress: boolean = false;
  signalrSubscription?: Subscription;
  suscription: Subscription;
  visiblePdf: boolean = false;

  constructor(public config: DynamicDialogConfig,
    private invoicingCutoffService: InvoicingCutoffService,
    private signalrService: SignalrService,
    private toastHandlerService: ToastHandlerService,
    @Optional() private ref: DynamicDialogRef) { }

  ngOnInit(): void {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.suscription = input$?.subscribe((inputs: any) => {
      this.invoicingCutoffId = inputs.invoicingCutoffId;
      this.branchName = inputs.branchName;
      this.invoicingCutoffCode = inputs.invoicingCutoffCode;
      this.invoicingCutoffDate = inputs.invoicingCutoffDate;
    });

    // Iniciar conexión SignalR y escuchar mensajes del backend
    this.signalrService.startConnection();
    this.signalrSubscription = this.signalrService.onMessage$
      .pipe(filter((msg): msg is Extract<BackendMessage, { type: BackendMessageType.ProgressUpdate }> =>
        msg.type === BackendMessageType.ProgressUpdate
      ), filter(msg => msg.data.jobId === this.progressJobId))
      .subscribe(msg => {
        const data = msg.data;
        this.progressValue = data.progress;
        this.progressCurrent = data.currentValue;
        this.progressTotal = data.totalValue;
        this.progressFinished = data.finished;
        this.progressStatusMessage = data.statusMessage ?? this.progressStatusMessage;

        if (data.finished) {
          this.progressJobId = null;
        }
      });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    this.listObservers.forEach(sub => sub.unsubscribe());
    this.signalrSubscription?.unsubscribe();
  }

  close() {
    this.preInvoicingAlerts = new Array<PreInvoicingAlertModel>();
    this.visiblePdf = false;
    this.showPreinvoicingProgress = false;
    const data: ModalObjectType = {};
    if (this.disabledStart) {
      data.params = { reload: true };
      this.disabledStart = false;
      this.ref.close(data);
    } else {
      data.params = { reload: false };
      this.ref.close(data);
    }
  }

  async downloadErrorsTxt() {
    if (!this.preInvoicingAlerts || this.preInvoicingAlerts.length === 0) {
      return;
    }

    this.invoicingCutoffService.exportPreInvoicingErrorsTxt(this.preInvoicingAlerts).subscribe({
      next: (blob: Blob) => {
        const file = new Blob([blob], { type: 'text/plain' });
        const url = window.URL.createObjectURL(file);
        const today = new Date();
        const formatDate = today.toISOString().split('T')[0];

        const a = document.createElement('a');
        a.href = url;
        a.download = `ErroresPreFacturacion_${formatDate}.txt`;
        a.click();

        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastHandlerService.showToast('error', 'Error al exportar el archivo.');
      }
    });
  }

  async preInvoicing() {
    this.disabledStart = true;
    this.disabledClose = true;
    this.showPreinvoicingProgress = true;
    this.progressStatusMessage = 'Inicializando...';
    this.progressValue = 0;
    this.progressCurrent = 0;
    this.progressTotal = 0;
    this.progressFinished = false;

    try {
      const { jobId } = await firstValueFrom(
        this.invoicingCutoffService.preInvoicingByInvoicingCutoffId(this.invoicingCutoffId)
      );

      this.progressJobId = jobId;

      // Suscribirse al grupo del job para recibir ProgressUpdate
      await this.signalrService.subscribeToJob(jobId);

      // Esperar a que SignalR indique que terminó y recibir el resultado
      const finalMsg = await firstValueFrom(
        this.signalrService.onMessage$.pipe(
          filter((msg): msg is Extract<BackendMessage, { type: BackendMessageType.ProgressUpdate }> =>
            msg.type === BackendMessageType.ProgressUpdate
          ),
          filter(msg => {
            return msg.data.jobId === jobId && msg.data.finished;
          })
        )
      );
      const result = finalMsg.data;

      // Desuscribirse del grupo del job
      this.signalrService.unsubscribeFromJob(jobId);
      this.progressJobId = null;

      this.loadPreInvoicingModel = {
        preInvoicingStateId: result.preInvoicingStateId,
        alerts: result.alerts ?? []
      } as LoadPreInvoicingModel;
      this.preInvoicingAlerts = this.loadPreInvoicingModel.alerts;

      if (result.preInvoicingStateId == PreInvoicingStateEnums.error) {
        this.visiblePdf = true;
        this.toastHandlerService.showToast(
          'error',
          'El proceso ha terminado con Errores, expórtelos a PDF si lo desea.'
        );
      } else if (result.preInvoicingStateId == PreInvoicingStateEnums.alerta) {
        this.visiblePdf = true;
        this.toastHandlerService.showToast(
          'error',
          'El proceso ha terminado con Alertas, expórtelos a PDF si lo desea.'
        );
      } else {
        if (result.hasError) {
          this.toastHandlerService.showToast(
            'error',
            result.statusMessage ?? 'El proceso ha terminado con errores.'
          );
        } else {
          this.visiblePdf = false;
          this.toastHandlerService.showToast(
            'success',
            'El proceso ha terminado exitosamente.'
          );
        }
      }
      this.disabledClose = false;

    } catch (error) {
      console.error('Error en preInvoicing:', error);
      this.showPreinvoicingProgress = false;
      this.progressJobId = null;
      this.disabledStart = false;
      this.disabledClose = false;
    }
  }

}
